import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PageLoading } from '@/components/ui/loading';
import { formatDate, getStatusBadgeClass, getStatusLabel } from '@/lib/utils';
import { Link } from 'react-router-dom';
import { Eye } from 'lucide-react';
import { ROLES } from '@/lib/permissions';

export default function PendingApprovalsPage() {
  const { hasRole } = useAuth();

  const { data: requirements, isLoading: reqLoading } = useQuery({
    queryKey: ['pending-approvals'],
    queryFn: async () => {
      const res = await api.get<{ success: boolean; data: any[] }>('/requirements/pending/approvals');
      return (res.data ?? []) as any[];
    },
  });

  const { data: actuals, isLoading: actLoading } = useQuery({
    queryKey: ['pending-approvals-actuals'],
    queryFn: async () => {
      const res = await api.get<{ success: boolean; data: any[] }>('/actual-status/pending/approvals');
      return (res.data ?? []) as any[];
    },
  });

  if (reqLoading || actLoading) return <PageLoading />;

  const title = hasRole(ROLES.CLUSTER_HEAD) ? 'Pending Final Approvals' : 'Pending Approvals';

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-heading font-bold text-primary">{title}</h1>

      <Tabs defaultValue="requirements">
        <TabsList>
          <TabsTrigger value="requirements">Requirements</TabsTrigger>
          <TabsTrigger value="actuals">Actual Labour</TabsTrigger>
        </TabsList>

        <TabsContent value="requirements">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Requirement #</TableHead>
                    <TableHead>Project</TableHead>
                    <TableHead>Start Date</TableHead>
                    <TableHead>Submitted By</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requirements && requirements.length > 0 ? requirements.map((req: any) => (
                    <TableRow key={req.id}>
                      <TableCell className="font-medium">{req.requirementNumber}</TableCell>
                      <TableCell>{req.projectName}</TableCell>
                      <TableCell>{formatDate(req.startDate)}</TableCell>
                      <TableCell>{req.createdByName}</TableCell>
                      <TableCell><Badge variant={getStatusBadgeClass(req.status) as any}>{getStatusLabel(req.status)}</Badge></TableCell>
                      <TableCell>
                        <Link to={`/requirements/${req.id}`}>
                          <Button variant="ghost" size="sm"><Eye size={14} className="mr-1" />Review</Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted">No pending approvals</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="actuals">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Actual #</TableHead>
                    <TableHead>Project</TableHead>
                    <TableHead>Work Date</TableHead>
                    <TableHead>Submitted By</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {actuals && actuals.length > 0 ? actuals.map((a: any) => (
                    <TableRow key={a.id}>
                      <TableCell className="font-medium">{a.actual_number}</TableCell>
                      <TableCell>{a.project_name}</TableCell>
                      <TableCell>{formatDate(a.work_date)}</TableCell>
                      <TableCell>{a.created_by_name}</TableCell>
                      <TableCell><Badge variant={getStatusBadgeClass(a.status) as any}>{getStatusLabel(a.status)}</Badge></TableCell>
                      <TableCell>
                        <Link to={`/actuals/${a.id}`}>
                          <Button variant="ghost" size="sm"><Eye size={14} className="mr-1" />Review</Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted">No pending approvals</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
